import { X } from 'lucide-react';
import { cn } from '../../../../lib/utils';
import { useTranslation } from 'react-i18next';
import {
  borderPrimary,
  semiBoldText,
  primaryText,
  largeTextSize,
  smallTextSize,
  secondaryText,
  whiteBG,
  baseTextSize,
  mediumText,
} from '../../../../styling/stylingHelper';
import { ColorPicker } from '../../../common/ColorPicker';

interface EditCategoryModalProps {
    isOpen: boolean;
    categoryId: string;
    categoryName: string;
    categoryColor: string;
    onCategoryNameChange: (name: string) => void;
    onCategoryColorChange: (color: string) => void;
    onClose: () => void;
    onSave: () => void;
}

const EditCategoryModal = ({
    isOpen,
    categoryName,
    categoryColor,
    onCategoryNameChange,
    onCategoryColorChange,
    onClose,
    onSave
}: EditCategoryModalProps) => {
    const { t } = useTranslation();

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 bg-black/50 dark:bg-black/60 flex items-center justify-center z-50 p-4"
            onClick={onClose}
        >
            <div
                className={cn(
                    "bg-white dark:bg-background-dark rounded-xl shadow-xl w-full max-w-lg border",
                    borderPrimary
                )}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="p-4">
                    {/* Header */}
                    <div className="flex justify-between items-start mb-4 relative">
                        <h2 className={cn(semiBoldText, largeTextSize, primaryText)}>
                            {t('costPrice.editCategoryModal.title')}
                        </h2>
                        <button
                            onClick={onClose}
                            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Input */}
                    <div className="mb-6">
                        <label className={cn(mediumText, baseTextSize, secondaryText, "mb-2 block")}>
                            {t('costPrice.editCategoryModal.categoryName')}
                        </label>
                        <input
                            type="text"
                            value={categoryName}
                            onChange={(e) => onCategoryNameChange(e.target.value)}
                            placeholder={t('costPrice.editCategoryModal.placeholder')}
                            className={cn(
                                "w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-100",
                                borderPrimary,
                                whiteBG,
                                primaryText
                            )}
                            autoFocus
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && categoryName.trim()) {
                                    onSave();
                                } else if (e.key === 'Escape') {
                                    onClose();
                                }
                            }}
                        />
                    </div>

                    {/* Color Picker */}
                    <div className="mb-6">
                        <ColorPicker
                            value={categoryColor}
                            onChange={onCategoryColorChange}
                            label={t('costPrice.editCategoryModal.categoryColor')}
                        />
                    </div>

                    {/* Actions */}
                    <div className="-mx-4 mt-4">
                        <div className="border-t border-borderPrimary dark:border-borderPrimary-dark w-full" />
                        <div className="flex justify-end gap-3 pt-4 px-4">
                            <button
                                onClick={onClose}
                                className={cn(
                                    smallTextSize,
                                    secondaryText,
                                    semiBoldText,
                                    "rounded-xl px-4 py-3",
                                    whiteBG,
                                    "border",
                                    borderPrimary,
                                    "hover:bg-gray-50 dark:hover:bg-backgroundDarker-dark",
                                    "flex-1"
                                )}
                            >
                                {t('common.cancel')}
                            </button>
                            <button
                                onClick={onSave}
                                disabled={!categoryName.trim()}
                                className={cn(
                                    smallTextSize,
                                    "text-white dark:text-white",
                                    semiBoldText,
                                    "bg-darkGreenColor dark:bg-darkGreenColor-dark rounded-xl px-4 py-3",
                                    "hover:bg-darkGreenColor/95",
                                    "flex-1",
                                    "disabled:opacity-50 disabled:cursor-not-allowed"
                                )}
                            >
                                {t('common.save')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditCategoryModal;
